import { useQuery } from '@tanstack/react-query';

import { getApiV5FirmsEmployees } from '@liscio/api';
import type {
  EmployeeSchemaDataItem,
  GetApiV5FirmsEmployeesParams,
} from '@liscio/api';

const MAX_PER_PAGE = 100;
// Revisit this approach when firms exceed 200 employees; at that point
// owner-list loads will need 3+ requests and should be fixed properly.
const MAX_EMPLOYEE_LIST_REQUESTS = 10;

export type EmployeeStatusFilter = 'active' | 'inactive' | 'all';

function mapStatusFilterToParams(
  filter: EmployeeStatusFilter | undefined = 'all'
): { active?: boolean } {
  switch (filter) {
    case 'active':
      return { active: true };
    case 'inactive':
      return { active: false };
    case 'all':
    default:
      return {};
  }
}

interface EmployeeListParams {
  enabled: boolean;
  perPage?: number;
  statusFilter?: EmployeeStatusFilter;
}

export const useEmployeeList = ({
  enabled,
  perPage,
  statusFilter = 'active',
}: EmployeeListParams) => {
  const effectivePerPage = Math.min(perPage ?? MAX_PER_PAGE, MAX_PER_PAGE);

  const params: GetApiV5FirmsEmployeesParams = {
    sort: 'name',
    per_page: effectivePerPage,
    ...mapStatusFilterToParams(statusFilter),
  };

  const { data, isLoading, isError, fetchStatus, isFetching } = useQuery({
    queryKey: ['/api/v5/firms/employees/all', params],
    enabled,
    staleTime: Infinity, // Prevent unnecessary refetches
    keepPreviousData: true, // Keep previous data while loading new data
    retry: false,
    queryFn: async ({ signal }) => {
      const employees: EmployeeSchemaDataItem[] = [];
      const seenCursors = new Set<string>();
      let response = await getApiV5FirmsEmployees(params, signal);
      let totalEntries = response.meta.total_entries || 0;
      let requestCount = 1;

      employees.push(...response.data);

      while (response.meta.next && response.data.length >= effectivePerPage) {
        if (requestCount >= MAX_EMPLOYEE_LIST_REQUESTS) {
          throw new Error('Employee pagination request limit exceeded');
        }

        if (seenCursors.has(response.meta.next)) {
          throw new Error('Employee pagination cursor repeated');
        }

        seenCursors.add(response.meta.next);
        response = await getApiV5FirmsEmployees({ next: response.meta.next }, signal);
        requestCount += 1;
        totalEntries = response.meta.total_entries || totalEntries;
        employees.push(...response.data);
      }

      return { employeeList: employees, totalEntries };
    },
  });

  const loading = (isLoading || isFetching) && fetchStatus !== 'idle';

  return {
    employeeList: data?.employeeList || [],
    employeeListLoading: loading,
    employeeListError: isError,
    totalEntries: data?.totalEntries || 0,
  };
};
